import { useState, useEffect } from "react"
import { useFormik } from "formik"
import { useParams, useNavigate } from "react-router-dom"
import { setHours, setMinutes } from "date-fns"

import { useUI } from "providers/UIProvider"
import { ILaundryFormInitialValues } from "utils/constants"
import { IRoles } from "types/shared"
import { LAUNDRY_FORM_INITIAL_VALUES } from "utils/constants"
import { getLaundryValidationSchema } from "validationSchemas/getLaundryValidationSchema"
import { useLaundryContext } from "providers/LaundryProvider/LaundryProvider"
import { IAdditionalService, ICarBodyType, ICarWashType } from "types/laundry"
import { formatCreateUpdateLaundryData } from "utils/formatCreateUpdateLaundryData"
import { ISelectOption } from "types/shared"
import moment from "moment"
import { toast } from "react-toastify"
import { useAppContext } from "providers/AppProvider/AppProvider"

interface ILaundriesFiltersState {
  opened: boolean
  closed: boolean
}

const useLaundry = () => {
  const params = useParams()
  const navigate = useNavigate()
  const addMode = Boolean(params.laundryId === "add-laundry")
  const { userRole } = useAppContext()
  const { T } = useUI()
  const { laundry, districts, createUpdateLaundry } = useLaundryContext()

  const [file, setFile] = useState<Blob | null>(null)
  const [coordinates, setCoordinates] = useState<any>(null)
  const [serviceData, setServiceData] = useState<any>({})
  const [discountTypes, setDiscounts] = useState<
    { id: number; name: string }[]
  >([])
  const [additionalServices, setAdditionalServices] = useState<
    IAdditionalService[]
  >([])
  const [districtOptions, setDistrictOptions] = useState<ISelectOption[]>([])

  const [filters, setFilters] = useState<ILaundriesFiltersState>({
    opened: false,
    closed: false,
  })

  const [selectedAdditionalServices, setSelectedAdditionalServices] = useState<
    number[]
  >([])

  const handleAddDiscount = (discount: { id: number; name: string }) => {
    setDiscounts([...discountTypes, discount])
  }

  const handleDeleteDiscount = (value: string) => {
    setDiscounts((prevData) => prevData.filter((val) => val.name !== value))
  }

  // const handleDeleteDiscount = (val: any) => {
  //   console.log(val)
  //   const updatedDiscountTypes = discountTypes.filter(
  //     (discountType) => discountType.name == val,
  //   )
  // }

  const handleAdditionalServiceClick = (id: number) => {
    if (selectedAdditionalServices.find((item) => item === id)) {
      const updatedSelectedAdditionalServices =
        selectedAdditionalServices.filter((item) => item !== id)
      setSelectedAdditionalServices(updatedSelectedAdditionalServices)
    } else {
      setSelectedAdditionalServices([...selectedAdditionalServices, id])
    }
  }
  const updateServiceData = (
    serviceKey: string,
    fieldKey: string,
    value: string,
  ) => {
    setServiceData({
      ...serviceData,
      [serviceKey]: {
        ...serviceData[serviceKey],
        [fieldKey]: value,
      },
    })
  }

  const updateFilters = (key: keyof ILaundriesFiltersState, value: boolean) =>
    setFilters((currentFilters) => ({ ...currentFilters, [key]: value }))

  const isQappUser =
    userRole && [IRoles.QAPP_ADMIN, IRoles.QAPP_MANAGER].includes(userRole)

  const onSubmit = (val: ILaundryFormInitialValues) => {
    const formData = new FormData()
    const invalidTimesList: any[] = Object.keys(serviceData).filter(
      (key) => serviceData[key].time === undefined,
    )

    if (invalidTimesList.length > 14) {
      toast.error("აირჩიეთ სერვისის დროები")
      return
    }
    const updatedFormData = formatCreateUpdateLaundryData({
      formData,
      formValues: val,
      coordinates,
      selectedAdditionalServices,
      serviceData,
      discountTypes,
    })
    updatedFormData.append("logo", (file as Blob) || values.logo)
    updatedFormData.append("id", laundry.id)
    createUpdateLaundry(updatedFormData)
  }

  const handleLogoChange = (e: any) => {
    setErrors({ ...errors, logo: "" })
    const file = e.target.files[0]

    if (file?.type !== "image/png") {
      setErrors({
        ...errors,
        logo: T("laundry.form.validations.logo_invalid_type"),
      })
      return false
    }

    const url: any = URL.createObjectURL(file)
    const reader = new Image()

    reader.onload = function () {
      if (reader.width !== 500 || reader.height !== 500) {
        setErrors({
          ...errors,
          logo: T("laundry.form.validations.logo_invalid_size"),
        })
        return false
      }
      const objectURL = URL.createObjectURL(file)

      setValues({ ...values, logo: objectURL })
    }

    setFile(file)

    reader.src = url
  }

  const removeLogo = () => setValues({ ...values, logo: "" })

  const handle24Click = () => {
    setValues((currentValues) => ({
      ...currentValues,
      is24: !currentValues.is24,
    }))
  }

  const handleMapChange = async (e: any) => {
    setValues({ ...values, address: e.target.value })
  }

  const initializeServiceData = (bodyTypes: ICarBodyType[]) => {
    const defaultServiceData: {
      [key: string]: { price: number; time: number }
    } = {}
    bodyTypes.map((bodyType: ICarBodyType) => {
      bodyType.washTypes.map((washType: ICarWashType) => {
        defaultServiceData[`body${bodyType.id}-wash${washType.id}`] = {
          price: washType.price || 0,
          time: washType.washTime || 0,
        }
        // To initialize edit form data
        values[`body${bodyType.id}Wash${washType.id}`] = {
          price: 0,
          time: 0,
        }
        values[`body${bodyType.id}Wash${washType.id}`].price =
          defaultServiceData[`body${bodyType.id}-wash${washType.id}`]?.price
        values[`body${bodyType.id}Wash${washType.id}`].time =
          defaultServiceData[`body${bodyType.id}-wash${washType.id}`]?.time
      })
    })
    setServiceData(defaultServiceData)
  }

  const handleDenyClick = () => {
    navigate("/laundries")
  }

  useEffect(() => {
    if (addMode && laundry) {
      resetForm()
      setAdditionalServices(laundry?.additionalServices)
    }
    if (laundry && !addMode) {
      const selectedAdditionalServices = laundry.additionalServices?.reduce(
        (acc: number[], AS: IAdditionalService) => {
          if (AS.isActive) {
            acc.push(AS.id)
          }

          return acc
        },
        [],
      )

      setValues({
        ...values,
        name: laundry.name,
        logo: laundry.logoUrl,
        address: laundry.address,
        phone: laundry.phoneNumber,
        idNumber: laundry.idNumber,
        bankAccount: laundry.bankAccount,
        email: laundry.email,
        is24: laundry.is24Houres,
        startTime: moment(
          laundry?.workStartTime || setHours(setMinutes(new Date(), 0), 8),
        ).toDate(),
        endTime: moment(
          laundry?.workEndTime || setHours(setMinutes(new Date(), 0), 22),
        ).toDate(),
        boxes: laundry.boxCount?.toString(),
        district: laundry.districtId?.toString(),
      })
      setAdditionalServices(laundry.additionalServices)
      setSelectedAdditionalServices(selectedAdditionalServices)
      setDiscounts(laundry.discountTypes)
      setCoordinates({
        lng: laundry.locationLongitude,
        lat: laundry.locationLatitude,
      })
      initializeServiceData(laundry.bodyTypes)
    }
  }, [laundry, addMode])

  useEffect(() => {
    if (districts) {
      const districtOptions = districts?.map((district) => ({
        label: district.name,
        value: district.id.toString(),
      }))
      setDistrictOptions(districtOptions)
    }
  }, [districts])

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setValues,
    setErrors,
    handleSubmit,
    resetForm,
  } = useFormik({
    initialValues: LAUNDRY_FORM_INITIAL_VALUES,
    validationSchema: () => getLaundryValidationSchema(T, isQappUser),
    onSubmit: onSubmit,
    validateOnChange: false,
  })

  return {
    form: {
      values,
      errors,
      touched,
      handleChange,
      handleLogoChange,
      handleBlur,
      handleSubmit,
      handle24Click,
      handleMapChange,
      setValues,
      handleDenyClick,
    },
    removeLogo,
    handleAdditionalServiceClick,
    selectedAdditionalServices,
    filters,
    updateFilters,
    additionalServices,
    coordinates,
    setCoordinates,
    serviceData,
    updateServiceData,
    districtOptions,
    discountTypes,
    handleAddDiscount,
    handleDeleteDiscount,
  }
}

export default useLaundry
